import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Loader2, Gift, X, Infinity, Zap, FileDown, Brain, MessageCircle, Shield } from "lucide-react";
import { useSubscriptionStore, PRICING, FREE_LIMITS, PREMIUM_LIMITS } from "@/store/useSubscriptionStore";
import { useAuth } from "@/contexts/AuthContext";
import app, { auth } from "@/lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: "university" | "storage" | "general" | "ai_analysis" | "compress" | "dm" | "auto_add" | "download" | "export" | "feedback" | "inbox_ai";
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

type PlanInterval = "quarterly" | "halfyearly" | "yearly";

// Firebase Functions (handles auth, CORS, and request/response format automatically)
const functions = getFunctions(app, 'us-central1');
const createRazorpayOrderFn = httpsCallable(functions, 'createRazorpayOrder');
const verifyRazorpayPaymentFn = httpsCallable(functions, 'verifyRazorpayPayment');

export function UpgradeDialog({ open, onOpenChange, reason = "general" }: UpgradeDialogProps) {
  const navigate = useNavigate();
  const { setPlan } = useSubscriptionStore();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<PlanInterval>("quarterly");

  const reasonMessages: Record<string, { title: string; desc: string }> = {
    university: { title: "University Limit Reached", desc: `You've reached the limit of ${FREE_LIMITS.maxUniversities} universities on the free plan.` },
    storage: { title: "Storage Full", desc: `You've reached the ${FREE_LIMITS.maxStorageMB}MB storage limit.` },
    general: { title: "Unlock with Premium", desc: "Get unlimited access and advanced features." },
    ai_analysis: { title: "Premium Feature", desc: "AI email analysis is exclusive to Premium." },
    compress: { title: "Compression Limit Reached", desc: "Upgrade for unlimited document compression." },
    dm: { title: "Premium Feature", desc: "Sending DM requests requires Premium." },
    auto_add: { title: "Auto-Add Limit Reached", desc: "Upgrade for more auto-adds per day." },
    download: { title: "Download Limit Reached", desc: "Upgrade for unlimited downloads." },
    export: { title: "Export Limit Reached", desc: "Upgrade for unlimited exports." },
    feedback: { title: "Feedback Limit Reached", desc: "Upgrade for more daily feedback submissions." },
    inbox_ai: { title: "Premium Feature", desc: "AI inbox analysis is exclusive to Premium." },
  };

  const msg = reasonMessages[reason] || reasonMessages.general;

  // Calculate expiry date - extends existing premium if active
  const getExpiryDate = (interval: PlanInterval): string => {
    const { plan, expiresAt, getDaysRemaining } = useSubscriptionStore.getState();
    const daysRemaining = getDaysRemaining();

    let startDate = new Date();
    if (plan === 'premium' && expiresAt && daysRemaining && daysRemaining > 0) {
      startDate = new Date(expiresAt);
    }

    if (interval === "quarterly") {
      startDate.setMonth(startDate.getMonth() + 3);
    } else if (interval === "halfyearly") {
      startDate.setMonth(startDate.getMonth() + 6);
    } else {
      startDate.setFullYear(startDate.getFullYear() + 1);
    }
    return startDate.toISOString();
  };

  // Load Razorpay script
  useEffect(() => {
    if (open && !razorpayLoaded) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      document.body.appendChild(script);
    }
  }, [open, razorpayLoaded]);

  const handleUpgrade = async () => {
    if (!user) {
      toast.error("Please sign in to upgrade");
      return;
    }

    if (!razorpayLoaded) {
      toast.error("Payment system is loading. Please try again.");
      return;
    }

    setIsLoading(true);

    const amount = selectedInterval === "quarterly"
      ? PRICING.quarterly
      : selectedInterval === "halfyearly"
        ? PRICING.halfyearly
        : PRICING.yearly;
    const expiresAt = getExpiryDate(selectedInterval);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Authentication required");
      }

      const orderResult = await createRazorpayOrderFn({
        amount,
        currency: "INR",
        notes: {
          plan: "premium",
          interval: selectedInterval,
          expiresAt,
        },
      });

      const orderData = orderResult.data as any;

      if (!orderData?.orderId || !orderData?.keyId) {
        throw new Error("Invalid order response");
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "ApplyWise",
        description: `Premium ${selectedInterval === "quarterly" ? "3 Months" : selectedInterval === "halfyearly" ? "6 Months" : "1 Year"} Subscription`,
        order_id: orderData.orderId,
        handler: async function (response: any) {
          try {
            const verifyResult = await verifyRazorpayPaymentFn({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: user.uid,
              interval: selectedInterval,
              expires_at: expiresAt,
            });

            const result = verifyResult.data as any;
            if (!result?.verified) {
              throw new Error("Payment verification failed");
            }

            if (!result?.saved) {
              console.error("Subscription not saved:", result?.error);
              throw new Error("Failed to save subscription");
            }

            setPlan("premium", selectedInterval, expiresAt);
            toast.success("Welcome to Premium! Enjoy unlimited access.");
            onOpenChange(false);
            setIsLoading(false);
          } catch (err) {
            console.error("Payment verification error:", err);
            toast.error("Payment verification failed. Please contact support.");
            setIsLoading(false);
          }
        },
        prefill: {
          email: user.email || "",
          name: user.displayName || "",
        },
        theme: {
          color: "#f59e0b",
        },
        modal: {
          ondismiss: function () {
            setIsLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response: any) {
        console.error("Payment failed:", response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setIsLoading(false);
      });
      rzp.open();
    } catch (error: any) {
      console.error("Upgrade error:", error);
      toast.error(error.message || "Failed to initiate payment");
      setIsLoading(false);
    }
  };

  const perMonthQuarterly = Math.round(PRICING.quarterly / 3);
  const perMonthHalfyearly = Math.round(PRICING.halfyearly / 6);
  const perMonthYearly = Math.round(PRICING.yearly / 12);

  const benefits = [
    { icon: Infinity, label: "Unlimited university tracking" },
    { icon: Zap, label: "Unlimited document compression" },
    { icon: FileDown, label: "Unlimited exports & downloads" },
    { icon: Brain, label: "AI email analysis (4/day)" },
    { icon: MessageCircle, label: "Send DM requests" },
    { icon: Shield, label: "Priority support" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Crown className="h-5 w-5 text-amber-500" />
            {msg.title}
          </DialogTitle>
          <DialogDescription className="text-xs">{msg.desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Benefits List */}
          <div className="space-y-2 p-3 rounded-lg bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20">
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <Crown className="h-3.5 w-3.5" />
              Premium Benefits
            </p>
            <ul className="space-y-1.5">
              {benefits.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2 text-xs">
                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Plan Toggle */}
          <div className="flex justify-center">
            <div className="inline-flex flex-wrap justify-center rounded-lg bg-muted p-1 gap-1">
              <button
                onClick={() => setSelectedInterval("quarterly")}
                className={cn(
                  "px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                  selectedInterval === "quarterly"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                3 Months
              </button>
              <button
                onClick={() => setSelectedInterval("halfyearly")}
                className={cn(
                  "px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                  selectedInterval === "halfyearly"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                6 Months
              </button>
              <button
                onClick={() => setSelectedInterval("yearly")}
                className={cn(
                  "px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors relative",
                  selectedInterval === "yearly"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                1 Year
                <Badge
                  variant="success"
                  className="absolute -top-2 -right-2 text-[8px] px-1"
                >
                  Best
                </Badge>
              </button>
            </div>
          </div>

          {/* Pricing */}
          <div className="text-center space-y-1">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold">
                ₹{selectedInterval === "quarterly"
                  ? PRICING.quarterly
                  : selectedInterval === "halfyearly"
                    ? PRICING.halfyearly
                    : PRICING.yearly}
              </span>
              <span className="text-sm text-muted-foreground">
                /{selectedInterval === "quarterly" ? "3 mo" : selectedInterval === "halfyearly" ? "6 mo" : "yr"}
              </span>
            </div>
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              ₹{selectedInterval === "quarterly"
                ? perMonthQuarterly
                : selectedInterval === "halfyearly"
                  ? perMonthHalfyearly
                  : perMonthYearly}/month
            </p>
          </div>

          {/* CTA */}
          <Button
            onClick={handleUpgrade}
            disabled={isLoading || !razorpayLoaded}
            className="w-full gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm"
            size="default"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crown className="h-4 w-4" />
            )}
            {isLoading
              ? "Processing..."
              : `Upgrade Now — ₹${selectedInterval === "quarterly"
                ? PRICING.quarterly
                : selectedInterval === "halfyearly"
                  ? PRICING.halfyearly
                  : PRICING.yearly}`
            }
          </Button>

          {/* Free Premium Offer */}
          <button
            onClick={() => {
              onOpenChange(false);
              navigate('/testimonials');
            }}
            className="flex items-center gap-2 p-2.5 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors w-full text-left"
          >
            <Gift className="h-4 w-4 text-primary shrink-0" />
            <div className="text-xs">
              <span className="font-medium">Get 14 days free!</span>{" "}
              <span className="text-muted-foreground">Submit a testimonial →</span>
            </div>
          </button>

          <p className="text-[10px] text-center text-muted-foreground">
            Secure payment via Razorpay • Cancel anytime
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
